import {ManipulationEditor} from "./ManipulationEditor";
import {TaskEditor} from "./TaskEditor";
import {calcClosestPoint, normalizeAngle} from "./CanvasUtils.ts";
import {PathHandler} from "./PathHandler";

const HIT_DISTANCE = 3;

/**
 * @typedef {Object} Line
 * @property {number} x1
 * @property {number} y1
 * @property {number} x2
 * @property {number} y2
 */
/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */


const HEX = "0123456789ABCDEF";

export class EdgeEditor extends ManipulationEditor {
	/**
	 * @param {ProcessEditor} parent
	 * @param {PConnection} connection
	 * @param {PCoords} fromCoords
	 * @param {PCoords} toCoords
	 */
	constructor(parent, connection, fromCoords, toCoords) {
		super(parent);
		this.con = connection;
		/** @type {PCoords} */
		this.fromCoords = fromCoords;
		/** @type {PCoords} */
		this.toCoords = toCoords;
		this.color = this.con.edge.id === "ELSE" ? "#888888" : "black";
		this.handlers = null;
	}

	get selected() {
		return super.selected;
	}

	set selected(s) {
		super.selected = s;
		if (s) {
			this.handlers = [];
			if (this.fromCoords.out) {
				const edgeCoords = this.fromCoords.out[this.con.edge.id];
				if (edgeCoords && edgeCoords.mp) {
					for (let i = 0; i < edgeCoords.mp.length; i++) {
						const p = edgeCoords.mp[i];
						this.handlers.push(new PathHandler(this, edgeCoords, i));
					}
				}
			}
		} else this.handlers = null;
	}


	hit(x, y) {
		if (this.handlers) {
			// If selected check handlers first
			for (let h of this.handlers) {
				if (h.hit(x, y)) return h;
			}
		}

		const segs = this.lineSegments();

		/** @type {ProcessEditor} */
		const ped = this.parent;
		return ped.calcInCanvas((ctx)=>{
			ctx.lineWidth = 6;

			/** @type {Line} */
			const line = {
				x1: segs[0].x,
				y1: segs[0].y,
				x2: 0, y2: 0
			};
			for (let i = 1; i < segs.length; i++) {
				line.x2 = segs[i].x;
				line.y2 = segs[i].y;
				let onLine = this.pointOnLineSegment(ctx, line, x, y, i-1);
				this.lastHit = onLine;
				if (onLine !== null) {
					return this;
				}
				line.x1 = line.x2;
				line.y1 = line.y2;
			}
			return null;
		});
	}

	/**
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {Line} line
	 * @param {number} x
	 * @param {number} y
	 * @return {Point|null}
	 */
	pointOnLineSegment(ctx, line, x, y, segmentIdx) {
		ctx.beginPath();
		ctx.moveTo(line.x1, line.y1);
		ctx.lineTo(line.x2, line.y2);
		const inStroke = ctx.isPointInStroke(x, y);
		if (inStroke) {
			if (TaskEditor.distance(x, y, {x: line.x1, y: line.y1}) < HIT_DISTANCE) {
				return {x: line.x1, y: line.y1, start: true, end: false, segment: segmentIdx };
			} else if (TaskEditor.distance(x, y, {x: line.x2, y: line.y2}) < HIT_DISTANCE) {
				return {x: line.x2, y: line.y2, start: false, end: true, segment: segmentIdx };
			} else {
				let xx = 0, yy = 0;
				if (line.y1 === line.y2) {
					yy = line.y1;
					xx = x;
				} else if (line.x1 === line.x2) {
					xx = line.x1;
					yy = y;
				} else {
					const cpt = calcClosestPoint({x:line.x1, y:line.y1}, {x:line.x2, y:line.y2}, {x:x, y: y});
					xx = Math.round(cpt.x);
					yy = Math.round(cpt.y);
				}
				return {x: xx, y: yy, start: false, end: false, segment: segmentIdx };
			}
		} else return null;
	}

	doubleClick(e, canvas, pos) {
		const h = this.hit(pos.x, pos.y);
		if (h === null) return false;
		if (h === this) {
			// double clicked on the path (but not handler) ... handler will be created
			if (this.lastHit) {
				this.addMiddlePoint(this.lastHit.segment, this.lastHit.x, this.lastHit.y);
				return true;
			}
		} else {
			// handler double-clicked
		}
	}

	addMiddlePoint(idx, x, y) {
		if (!this.fromCoords.out) this.fromCoords.out = {};
		let mps;
		if (!this.fromCoords.out[this.con.edge.id]) this.fromCoords.out[this.con.edge.id] = {};
		const edgeCoords = this.fromCoords.out[this.con.edge.id];
		if (!Array.isArray(edgeCoords.mp)) edgeCoords.mp = [];
		edgeCoords.mp.splice(idx, 0, { x: x, y: y });
		this.handlers.push(new PathHandler(this, edgeCoords, idx));
	}

	/**
	 * Calculate line segments of the connection
	 * @return {Point[]} array of points
	 */
	lineSegments() {
		const segs = [];
		segs.push({ x: this.fromCoords.x, y: this.fromCoords.y });
		if (this.fromCoords.out) {
			const edgeCoords = this.fromCoords.out[this.con.edge.id];
			if (edgeCoords && edgeCoords.mp) {
				for (let p of edgeCoords.mp) {
					segs.push(p);
				}
			}
		}
		segs.push({ x: this.toCoords.x, y: this.toCoords.y });
		return segs;
	}

	draw(ctx) {
		ctx.save();

		ctx.strokeStyle = this.selected ? S.HI_COLORS[8] : this.color;
		ctx.lineWidth = this.lineWidth;

		const segs = this.lineSegments();

		ctx.beginPath();
		ctx.moveTo(segs[0].x, segs[0].y);
		for (let i = 1; i < segs.length; i++) {
			ctx.lineTo(segs[i].x, segs[i].y);
		}
		ctx.stroke();

		const lastSegment = {
			x1 : segs[segs.length-2].x,
			y1 : segs[segs.length-2].y,
			x2 : segs[segs.length-1].x,
			y2 : segs[segs.length-1].y
		};

		let arrow = this.findArrowPosition(lastSegment);
		if (arrow !== null) {
			ctx.save();
			ctx.fillStyle = this.selected ? S.HI_COLORS[8] : this.color;
			ctx.translate(arrow.tipX, arrow.tipY);
			ctx.rotate(arrow.angle);
			ctx.translate(-1*arrow.tipX, -1*arrow.tipY);
			ctx.beginPath();
			ctx.moveTo(arrow.tipX, arrow.tipY);
			ctx.lineTo(arrow.tipX - 16, arrow.tipY - 8);
			ctx.lineTo(arrow.tipX - 12, arrow.tipY);
			ctx.lineTo(arrow.tipX - 16, arrow.tipY + 8);
			ctx.closePath();
			ctx.fill();
			ctx.restore();
		}
		if (this.con.edge.name) {
			const pck = this.pickEdgeNameSegment(segs);
			let angle = normalizeAngle(Math.atan2(pck.y2 - pck.y1, pck.x2 - pck.x1));
			let flip = false;
			if (Math.abs(angle) > Math.PI/2) {
				angle += angle < 0 ? Math.PI : -1*Math.PI;
				flip = true;
			}
			ctx.save();
			const anchor = {};
			switch (pck.align) {
				case "start":
					anchor.x = pck.x1;
					anchor.y = pck.y1;
					ctx.textAlign = flip ? "right" : "left";
					break;
				case "end":
					anchor.x = pck.x2;
					anchor.y = pck.y2;
					ctx.textAlign = flip ? "left" : "right";
					break;
				default:
				case "center":
					anchor.x = (pck.x1 + pck.x2)/2;
					anchor.y = (pck.y1 + pck.y2)/2;
					ctx.textAlign = "center";
					break;
			}
			ctx.translate(anchor.x, anchor.y);
			ctx.rotate(angle);
			ctx.translate(-1*anchor.x, -1*anchor.y);
			ctx.fillText(this.con.edge.name, anchor.x, anchor.y-5);
			ctx.restore();
		}
		ctx.restore();
	}

	/**
	 * @param {Point[]} segs
	 */
	pickEdgeNameSegment(segs) {
		let picked = this.pickLongest(segs);
		return picked;
	}

	pickLongest(segs) {
		let foundLength = -1000;
		let foundIdx = 0;
		for (let i = 1; i < segs.length; i++) {
			let len = Math.sqrt(Math.pow(segs[i].x - segs[i-1].x, 2) + Math.pow(segs[i].y - segs[i-1].y, 2));
			if (i === 1 || i === (segs.length-1)) len -= 30;	// first and last segment is shorter because of node
			if (len > foundLength) {
				foundLength = len;
				foundIdx = i;
			}
		}
		return this.foundNameSegment(segs, foundIdx);
	}

	foundNameSegment(segs, idx) {
		const first = idx === 1;
		const last = idx === (segs.length-1);
		let a = "center";
		if (first && !last) a = "end";
		if (!first && last) a = "start";
		return {
			x1: segs[idx-1].x,
			y1: segs[idx-1].y,
			x2: segs[idx].x,
			y2: segs[idx].y,
			align: a
		};
	}

	drawHandlers(ctx, pixelWidth = 1) {
		if (this.handlers) {
			for (let h of this.handlers) {
				h.draw(ctx, pixelWidth);
			}
		}
	}

	findArrowPosition(line) {
		const bbox = {
			left: this.toCoords.x - (this.toCoords.w / 2),
			top: this.toCoords.y - (this.toCoords.h / 2),
			right: this.toCoords.x + (this.toCoords.w / 2),
			bottom: this.toCoords.y + (this.toCoords.h / 2),
		};

		const lineH = line.y2 - line.y1;
		const lineW = line.x2 - line.x1;
		let angle;
		if (lineW === 0) {
			if (lineH > 0) angle = Math.PI/2;
			else angle = Math.PI/-2;
		} else angle = Math.atan2(lineH, lineW);
		//const lineL = Math.sqrt(Math.pow(lineH, 2) + Math.pow(lineW, 2));

		/** @type {ProcessNodeEditor} */
		const toEditor = this.parent.findNodeEditor(this.con.to);
		if (toEditor !== null) return toEditor.calcIncomingArrowTip(angle);
		else return null;
	}
}